const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

export function parseDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function two(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** "7 set, 14:41" */
export function dateTimeShort(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  if (!d) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${two(d.getHours())}:${two(d.getMinutes())}`;
}

/** "7 set" */
export function dateShort(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  if (!d) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "07/09/2026" */
export function dateBR(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  if (!d) return "—";
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** "14:41:07" */
export function timeHMS(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  if (!d) return "—";
  return `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
}

/** "14:41" */
export function timeHM(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  if (!d) return "—";
  return `${two(d.getHours())}:${two(d.getMinutes())}`;
}

/** "Setembro" */
export function monthName(d: Date): string {
  const names = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  return names[d.getMonth()] ?? "";
}

/** "2026-09" */
export function yyyymm(d: Date): string {
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}`;
}

/** "3 dias", "2 h", "12 min" */
export function ageSince(v: string | Date | null | undefined, now: Date = new Date()): string {
  const d = parseDate(v);
  if (!d) return "—";
  const ms = now.getTime() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${Math.max(0, min)} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} dias`;
}

/** Segundos -> "9 min", "27 min", "1 h 05" */
export function durationSec(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || Number.isNaN(sec)) return "—";
  if (sec < 60) return `${Math.round(sec)} s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h} h ${two(min % 60)}`;
}

export function formatCNPJ(c: string): string {
  const d = c.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

export function formatCPF(c: string): string {
  const d = c.replace(/\D/g, "").slice(0, 11);
  return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export function shortHash(h: string | null | undefined, head = 8, tail = 4): string {
  if (!h) return "—";
  if (h.length <= head + tail + 1) return h;
  return `${h.slice(0, head)}…${h.slice(-tail)}`;
}

export function fileSize(bytes: number | null | undefined): string {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? "";
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : parts[0]?.[1] ?? "";
  return (a + b).toUpperCase();
}
